import React from 'react';
import { Loader } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { asCurrency } from '../../shared/utils/dashboard-page.utils';
import classes from './dashboard-header.module.scss';

export type DashboardHeaderProps = {
  headerDateText: string;
  setIsFilterDialogOpen: (open: boolean) => void;
  anchorMonthSalesExTips: number;
  anchorMonthLabel: string;
  loading: boolean;
  isLightMode: boolean;
  onToggleThemeMode: () => void;
  onOpenAssistant?: () => void;
};

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  headerDateText,
  setIsFilterDialogOpen,
  anchorMonthSalesExTips,
  anchorMonthLabel,
  loading,
  isLightMode,
  onToggleThemeMode,
  onOpenAssistant,
}) => {
  return (
  <section className={[classes.hero, isLightMode ? classes.heroLight : ''].filter(Boolean).join(' ')}>
    <div className={classes.heroCopy}>
      <Button
        variant="cancel"
        className={classes.heroDateButton}
        onClick={() => setIsFilterDialogOpen(true)}
        ariaLabel="בחירת פילטר תאריך"
        dataTestId="dashboard-header-date"
      >
        <span className={classes.heroChevron}>⌄</span>
        <h1 className={classes.heroTitle}>{headerDateText}</h1>
        <span className={classes.heroCalendar} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4.5" width="18" height="16" rx="3" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="8" y1="2.5" x2="8" y2="6" />
            <line x1="16" y1="2.5" x2="16" y2="6" />
          </svg>
        </span>
      </Button>
      <div className={classes.heroMetricLine}>
        <span className={classes.heroText}>
          <span className={classes.heroMetricLabel}>מכירות לא כולל טיפים</span>
          <span className={classes.heroMetricLabelShort}>מכירות חודשי (ללא טיפים)</span>
          <span className={classes.heroMetricMonth}>{anchorMonthLabel}</span>
        </span>
        <span className={classes.metricPill}>{asCurrency(anchorMonthSalesExTips)}</span>
      </div>
    </div>
    <div className={classes.heroActions}>
      <div className={classes.heroControlsGroup}>
        <Button
          variant="cancel"
          className={classes.askAiButton}
          ariaLabel="Ask AI"
          dataTestId="dashboard-header-ask-ai"
          onClick={onOpenAssistant}
          disabled={!onOpenAssistant}
        >
          <span className={classes.askAiIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 0Q13.5 10.5 24 12Q13.5 13.5 12 24Q10.5 13.5 0 12Q10.5 10.5 12 0Z" />
            </svg>
          </span>
          <span>Ask AI</span>
        </Button>
        <Button
          variant="cancel"
          className={classes.themeToggleButton}
          onClick={onToggleThemeMode}
          ariaLabel={isLightMode ? 'Switch to dark mode' : 'Switch to light mode'}
          dataTestId="dashboard-header-theme-toggle"
        >
          <span
            className={[classes.themeToggleTrack, isLightMode ? classes.themeToggleTrackOn : '']
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          >
            <span className={classes.themeToggleThumb}>
              <svg
                className={classes.themeToggleIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4.2" />
                <line x1="12" y1="2.6" x2="12" y2="5" />
                <line x1="12" y1="19" x2="12" y2="21.4" />
                <line x1="2.6" y1="12" x2="5" y2="12" />
                <line x1="19" y1="12" x2="21.4" y2="12" />
                <line x1="5.4" y1="5.4" x2="7.1" y2="7.1" />
                <line x1="16.9" y1="16.9" x2="18.6" y2="18.6" />
                <line x1="5.4" y1="18.6" x2="7.1" y2="16.9" />
                <line x1="16.9" y1="7.1" x2="18.6" y2="5.4" />
              </svg>
            </span>
          </span>
        </Button>
      </div>
      {loading ? <Loader size="sm" /> : null}
    </div>
  </section>
  );
};
